<template>
  <div class="column is-full" v-if="reservation && isVisibleFromFilter">
    <div class="card mx-5" :data-id="reservation.requestId">
      <header class="card-header">
        <p class="card-header-title">
          {{ getHotelNameWithCity(reservation.hotel) }}
        </p>
        <p class="card-header-title is-justify-content-center">
          <i class="fa fa-person"></i>
          &nbsp;{{ getReservationGuests() }}
        </p>

        <p class="card-header-title is-justify-content-flex-end">
          <span>
            <i :class="getIcon(reservation.reservationType)"></i>
            {{ reservation.reservationType ?? "Onbekend" }}
          </span>
        </p>

        <p class="card-header-title is-justify-content-flex-end">
          <span>
            <i class="fa fa-moon"> </i>
            {{ getReservationNights() }}
            |
            {{ getReservationRangeAsString() }}
          </span>
        </p>
      </header>
      <div class="card-content">
        <div class="columns">
          <div class="column content">
            <table>
              <tbody>
                <tr v-if="getReference()">
                  <th>{{ $t("projectNumber") }}</th>
                  <td>{{ getReference() }}</td>
                </tr>
                <tr v-if="reservation.booker">
                  <th>{{ $t("booker") }}</th>
                  <td>
                    {{ reservation?.booker.firstName }}
                    {{ reservation?.booker.lastName }}
                  </td>
                </tr>
                <tr>
                  <th>{{ $t("bookingDate") }}</th>
                  <td>
                    {{ formatDate(reservation.bookingDate) }}
                  </td>
                </tr>
                <tr v-if="reservation?.businessUnit?.name">
                  <th>{{ getBusinessUnitLabel() }}</th>
                  <td>{{ reservation.businessUnit.name }}</td>
                </tr>
                <tr>
                  <th>{{ $t("hotelAddress") }}</th>
                  <td>
                    {{ reservation.hotel.street }} <br />
                    {{ reservation.hotel.postalCode }}
                    {{ reservation.hotel.city }}<br />
                    {{ reservation.hotel.country }}
                  </td>
                </tr>
                <tr
                  v-for="field in reservation.customFields"
                  v-bind:key="field.name"
                >
                  <th>
                    {{ getFieldNameTranslation(field) }}
                  </th>
                  <td>
                    {{ getFieldValueTranslation(field, field.value) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="column content">
            <template v-if="cancellationGuest">
              <h5 style="display: block; width: 100%">
                {{
                  $t("requestCancellation", {
                    firstname: cancellationGuest.firstName,
                    lastname: cancellationGuest.lastName
                  })
                }}

                <button
                  class="button is-warning is-small is-right"
                  style="float: right"
                  @click="cancellationGuest = null"
                >
                  {{ $t("back") }}
                </button>
              </h5>

              <table width="100%">
                <tbody>
                  <tr
                    v-for="d in getCancellableDates(cancellationGuest)"
                    :key="d.date"
                  >
                    <td colspan="2">
                      <label :for="d.id">
                        <input
                          type="checkbox"
                          v-model="cancellingDates"
                          v-if="
                            getCancellableDates(cancellationGuest).length > 1
                          "
                          :value="d.id"
                          :id="d.id"
                          checked="true"
                        />

                        {{ getFormattedDate(d.date) }}
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td width="50%">
                      <button
                        :disabled="cancellingDates.length == 0"
                        v-if="getCancellableDates(cancellationGuest).length > 1"
                        class="button is-danger is-small"
                        @click="cancelSelected()"
                      >
                        {{ $t("requestCancellationSelected") }}
                      </button>
                    </td>
                    <td>
                      <button
                        class="button is-danger is-small"
                        @click="cancelAll()"
                      >
                        {{ $t("requestCancellationAll") }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <table v-else>
              <thead>
                <tr>
                  <th>{{ $t("guest") }}</th>
                  <th>{{ $t("stay") }}</th>
                  <th>{{ $t("status") }}</th>
                  <th>{{ $t("costs") }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="g in getGuests()"
                  v-bind:key="g.name"
                  :class="{ 'all-cancelled': g.allDatesCancelled }"
                >
                  <td width="20%">
                    <b>{{ g.employee.firstName }} {{ g.employee.lastName }}</b>
                  </td>
                  <td width="30%">
                    {{ getDateRangeForEmployee(g) }}
                  </td>
                  <td width="30%">
                    {{ g.status }}
                  </td>
                  <td width="15%">
                    {{ getTotalPriceForEmployee(g) }}
                  </td>
                  <td width="5%">
                    <div
                      class="c-button c-button--red c-button--tiny"
                      style="text-align: center"
                      @click="requestCancellation(g)"
                      v-if="isCancellable(g)"
                      :title="
                        $t('requestCancellation', {
                          firstname: g.firstName,
                          lastname: g.lastName
                        })
                      "
                    >
                      <i class="fa-solid fa-xmark is-danger"></i>
                    </div>

                    <i
                      :title="
                        g.allDatesCancelled
                          ? $t('allDatesCancelled')
                          : $t('hasCancelledDates')
                      "
                      class="fa-solid fa-ban"
                      style="cursor: pointer"
                      v-if="g.hasCancelledDates"
                    ></i>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>
                    <b>{{ $t("total") }}</b>
                  </td>
                  <td width="20%" colspan="2">
                    {{ getTotalPriceForEmployees() }}
                  </td>
                </tr>
              </tbody>
            </table>
            <br />
          </div>
        </div>
      </div>
      <div class="card-footer is-justify-content-flex-end">
        <div class="buttons has-addons is-right mb-0 p-2">
          <template v-if="hasKnownhotel">
            <label class="px-6 mb-0">
              <input type="checkbox" v-model="rebookOptions.sameGuest" />
              {{ $t("rebook.sameGuest") }}
            </label>
            <label class="px-6 mb-0" v-if="reservation.businessUnit?.id">
              <input type="checkbox" v-model="rebookOptions.sameBusinessUnit" />
              {{ tbu("rebook.sameBusinessUnit") }}
            </label>

            <label class="px-6 mb-0" v-if="isFutureReservation">
              <input type="checkbox" v-model="rebookOptions.sameDate" />
              {{ $t("rebook.sameDate") }}
            </label>

            <button
              class="c-button c-button--primary c-button--small"
              @click="rebook()"
            >
              {{ $t("rebook.rebook") }}
            </button>
            <div
              class="is-warning button"
              v-if="rebookOptions.sameDate && rebookOptions.sameGuest"
            >
              {{ $t("rebook.doubleWarning") }}
            </div>
          </template>
          <div class="is-warning button" v-else>
            <i class="fa-solid fa-triangle-exclamation u-text-color-orange"></i>
            &nbsp;
            {{ $t("reservationoverview.externalBooking") }}
          </div>
        </div>
      </div>
    </div>
    <div class="modal" :class="{ 'is-active': showConfirmation }">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t("areYouSure") }}</p>
          <button
            @click="
              confirmationDates = [];
              cancellingDates = [];
              showConfirmation = false;
            "
            class="delete"
            aria-label="close"
          ></button>
        </header>
        <section class="modal-card-body">
          {{ $t("confirmationTextCancellationRequest") }}
        </section>
        <footer class="modal-card-foot">
          <button
            @click="confirmRequestCancellation()"
            class="button is-warning"
          >
            {{ $t("confirmCancellationRequest") }}
            <img
              width="50"
              height="60"
              src="@/assets/images/loader.svg"
              v-if="isCancelling"
            />
          </button>
          <button
            @click="
              confirmationDates = [];
              cancellingDates = [];
              showConfirmation = false;
            "
            class="button"
          >
            {{ $t("noGoBack") }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>
<script>
import ReservationHelper, {
  isNowHappening,
  isFutureReservation,
  getDateRangeForReservation
} from "@/helpers/reservation.helper.js";

import moment from "moment";

const canceledStatuses = [
  "Geannuleerd",
  "Cancelled",
  "Cancellation",
  "Annulering",
  "Afgekeurd",
  "Rejected"
];

export default {
  name: "ReservationItem",
  data() {
    return {
      rebookOptions: {
        sameGuest: false,
        sameDate: false,
        sameBusinessUnit: false
      },
      cancellingDates: [],
      cancellationGuest: null,
      showConfirmation: false,
      confirmationDates: [],
      isCancelling: false,
      chunkedReservations: []
    };
  },
  props: {
    reservation: {
      type: Object,
      default: () => {}
    },
    reservationType: {
      type: String,
      default: "Hotel"
    },
    visibleItems: {
      type: String,
      default: "ALL"
    }
  },
  methods: {
    getCancellableDates(guest) {
      return guest.dates;
    },
    dateIsCancellable(d) {
      return ReservationHelper.dateIsCancellable(d);
    },
    requestCancellation(guest) {
      this.cancellationGuest = guest;
    },
    cancelSelected() {
      this.cancelDates(this.cancellingDates);
    },
    cancelAll() {
      let allDates = this.getCancellableDates(this.cancellationGuest).map(
        (d) => d.id
      );
      this.cancelDates(allDates);
    },
    cancelAllEmployees() {
      let dateIds = [];
      this.reservation.guestsWithDates.forEach((gwd) => {
        let allDates = this.getCancellableDates(gwd.employee).map((d) => d.id);
        dateIds = dateIds.concat(allDates);
      });
      this.cancelDates(dateIds);
    },
    cancelDates(dates) {
      this.confirmationDates = dates;
      this.showConfirmation = true;
    },
    cancelCancellation() {
      this.confirmationDates = [];
      this.cancellingDates = [];
      this.showConfirmation = false;
      this.$emit("cancelledItems");
    },
    async confirmRequestCancellation() {
      this.isCancelling = true;
      await ReservationHelper.cancelReservations(this.confirmationDates);
      this.isCancelling = false;
      this.cancelCancellation();
    },
    rebook() {
      this.$emit("rebook", {
        reservation: this.reservation,
        dateRange: this.reservationDateRange,
        adults: this.reservation.guestsWithDates.length,
        employees: this.reservation.guestsWithDates
          .map((gwd) => gwd.employee.id)
          .join(","),
        rebookOptions: this.rebookOptions
      });
    },
    getReference() {
      try {
        return this.reservation.customFields.find(
          (f) => f.name == "Reference__c"
        ).value;
      } catch (e) {
        return null;
      }
    },
    getTotalPriceForEmployee(employee, status) {
      let totalPrice = employee.dates.reduce(
        (prev, current) => (prev += current.costs),
        0
      );
      return this.toPriceLabel(totalPrice);
    },
    getTotalPriceForEmployees() {
      let reservations = this.reservation.guestsWithDates;
      let total = 0;
      reservations.forEach((b) => {
        total += b.dates.reduce((prev, current) => (prev += current.costs), 0);
      });
      return this.toPriceLabel(total);
    },
    getReservationForEmployee(employee) {
      return this.reservation.guestsWithDates.find(
        (gwd) => gwd.employee.id === employee.id
      );
    },
    getDateRangeForEmployee(employee) {
      let startDate = new moment(employee.dates[0].date);
      let endDate = new moment(employee.dates[employee.dates.length - 1].date);

      return (
        this.formatDateLabel(startDate) +
        " - " +
        this.formatDateLabel(endDate.add(1, "days"))
      );
    },
    isCancellable(guest) {
      return (
        guest.status != "" &&
        !canceledStatuses.some((status) =>
          guest.status.toLowerCase().includes(status.toLowerCase())
        )
      );
    },
    getGuests() {
      if (this.chunkedReservations.length > 0) {
        return this.chunkedReservations;
      }

      let today = moment();

      // Divide the guests into chunks based on their status
      this.chunkedReservations = this.reservation.guestsWithDates.flatMap(
        (gwd) => {
          var status = gwd.dates.map((d) => d.status);

          let chunks = [];
          let currentStatus = status[0];
          let startIdx = 0;
          for (let i = 1; i < status.length; i++) {
            if (status[i] !== currentStatus) {
              chunks.push({
                employee: gwd.employee,
                status: currentStatus,
                start: startIdx,
                end: i - 1
              });
              currentStatus = status[i];
              startIdx = i;
            }
          }

          chunks.push({
            employee: gwd.employee,
            status: currentStatus,
            start: startIdx,
            end: status.length - 1
          });

          return chunks.map((chunk) => {
            return {
              employee: chunk.employee,
              status: chunk.status,
              dates: gwd.dates.slice(chunk.start, chunk.end + 1).map((d) => {
                return {
                  date: d.date,
                  costs: d.costs,
                  status: d.status,
                  id: d.id
                };
              }),
              allDatesCancelled: gwd.dates.every((d) =>
                canceledStatuses.some((status) =>
                  d.status.toLowerCase().includes(status.toLowerCase())
                )
              ),
              hasCancallabeDates: gwd.dates.some((date) => {
                let isInFuture = moment(date.date).isAfter(today);
                if (!isInFuture || date.status == "") {
                  return false;
                }

                return (
                  date.status != "" &&
                  !canceledStatuses.some((status) =>
                    date.status.toLowerCase().includes(status.toLowerCase())
                  )
                );
              })
            };
          });
        }
      );

      return this.chunkedReservations;
    },
    getReservationGuests() {
      let guests = this.getGuests();

      // Return unique guest names
      return [
        ...new Set(
          guests.map(
            (emp) => `${emp.employee.firstName} ${emp.employee.lastName}`
          )
        )
      ].join(", ");
    },
    getReservationNights() {
      try {
        return this.reservation.guestsWithDates[0].dates.length;
      } catch (e) {
        return 1;
      }
    },
    getReservationRangeAsString() {
      let { startDate, endDate } = this.reservationDateRange;
      startDate = new moment(startDate);
      let formatted = startDate.format("D MMMM yyyy");
      return formatted;
    },
    getFormattedDate(date) {
      return new moment(date).format("ddd D MMMM yyyy");
    },
    getIcon(type) {
      const icons = {
        Hotel: "fa fa-building",
        Vlucht: "fa fa-plane",
        Vergaderarrangement: "fa fa-car"
      };
      return icons[type] || "fa fa-question-circle";
    }
  },
  computed: {
    hasKnownhotel() {
      return this.reservation.hotel.id;
    },
    isVisibleFromFilter() {
      let filter = this.visibleItems;
      switch (filter) {
        case "FUTURE":
          return isFutureReservation(this.reservation, "hotel");
        case "CURRENT":
          return isNowHappening(this.reservation, "hotel");
        default:
          return true;
      }
    },
    reservationDateRange() {
      return getDateRangeForReservation(this.reservation, "hotel");
    }
  }
};
</script>

<style lang="scss" scoped>
.all-cancelled {
  text-decoration: line-through;
}
</style>
